import axios from 'axios';
import { Config } from '../config';

const authConfig = () => ({
  headers: {
    access_token: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  },
});

const wirelessApi = {
  // --- Catalogue ------------------------------------------------------------
  async getTests() {
    const { data } = await axios.get(`${Config.APIBaseURL}/wireless/tests`, authConfig());
    return data;
  },

  // --- Targets --------------------------------------------------------------
  async getTargets(engagementId) {
    const { data } = await axios.get(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/targets`,
      authConfig(),
    );
    return data;
  },

  /**
   * Authorising a target is what permits testing against it, so it is a
   * deliberate per-row action rather than a bulk toggle.
   */
  async updateTarget(targetId, changes) {
    const { data } = await axios.patch(
      `${Config.APIBaseURL}/wireless/target/${targetId}`,
      changes,
      authConfig(),
    );
    return data;
  },

  // --- Survey control -------------------------------------------------------
  async startSurvey(engagementId, iface = 'wlan0') {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/survey/start?interface=${encodeURIComponent(iface)}`,
      {},
      authConfig(),
    );
    return data;
  },

  async stopSurvey(engagementId) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/survey/stop`,
      {},
      authConfig(),
    );
    return data;
  },

  // --- Active scan ----------------------------------------------------------
  /**
   * One active scan. Sends directed probes, so it sees what a passive capture
   * cannot: 802.11w state, the AKM suite, WPS, and the SSIDs of networks that
   * hide them from beacons. Briefly interrupts the survey — it needs the radio
   * in managed mode.
   */
  async runScan(engagementId, iface = 'wlan0') {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/scan?interface=${encodeURIComponent(iface)}`,
      {},
      authConfig(),
    );
    return data;
  },

  /** Stations observed during the survey. */
  async getClients(engagementId, associatedOnly = false) {
    const { data } = await axios.get(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/clients?associated_only=${associatedOnly}`,
      authConfig(),
    );
    return data;
  },

  // --- Rules of engagement --------------------------------------------------
  async getRoE(engagementId) {
    const { data } = await axios.get(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/roe`,
      authConfig(),
    );
    return data;
  },

  async updateRoE(engagementId, changes) {
    const { data } = await axios.put(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/roe`,
      changes,
      authConfig(),
    );
    return data;
  },

  // --- Test runs ------------------------------------------------------------
  async getRuns(engagementId) {
    const { data } = await axios.get(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/runs`,
      authConfig(),
    );
    return data;
  },

  /**
   * A refused run comes back 200 with status 'not_authorized' rather than an
   * error: "not performed, because deauthentication was not authorised" is a
   * statement the report has to make, and an exception leaves no trace of it.
   */
  async createRun(engagementId, { test_id, target_id = null, notes = null }) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/runs`,
      { test_id, target_id, notes },
      authConfig(),
    );
    return data;
  },

  async updateRun(runId, changes) {
    const { data } = await axios.patch(
      `${Config.APIBaseURL}/wireless/run/${runId}`,
      changes,
      authConfig(),
    );
    return data;
  },

  async promoteRun(runId, overrides = {}) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/run/${runId}/promote`,
      overrides,
      authConfig(),
    );
    return data;
  },

  /**
   * Hand an active test to the agent. Returns as soon as the agent accepts it —
   * these run for minutes to half an hour, so the result is collected by poll().
   */
  async dispatchRun(runId, iface = 'wlan0') {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/run/${runId}/dispatch?interface=${encodeURIComponent(iface)}`,
      {},
      authConfig(),
    );
    return data;
  },

  /**
   * Stop a dispatched test early. The run stays running: the agent still
   * analyses whatever was captured, so the next poll collects a real verdict
   * over a shorter window rather than discarding the evidence.
   */
  async stopRun(runId) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/run/${runId}/stop`,
      {},
      authConfig(),
    );
    return data;
  },

  /** Idempotent: a test still executing comes back unchanged. */
  async pollRun(runId) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/run/${runId}/poll`,
      {},
      authConfig(),
    );
    return data;
  },

  /**
   * One unattended pass: survey, scan, evaluators, captures, deauth last.
   * Returns immediately — the campaign outlives the request, so progress is
   * polled rather than awaited.
   */
  async startCampaign(engagementId, options = {}) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/campaign`,
      options,
      authConfig(),
    );
    return data;
  },

  /** The latest campaign, or null. */
  async getCampaign(engagementId) {
    const { data } = await axios.get(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/campaign`,
      authConfig(),
    );
    return data;
  },

  async stopCampaign(campaignId) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/campaign/${campaignId}/stop`,
      {},
      authConfig(),
    );
    return data;
  },

  /** Decides W-01..W-04 from the survey. Re-runnable as targets get authorised. */
  async evaluateRecon(engagementId) {
    const { data } = await axios.post(
      `${Config.APIBaseURL}/wireless/engagement/${engagementId}/runs/evaluate-recon`,
      {},
      authConfig(),
    );
    return data;
  },
};

export default wirelessApi;
